import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { AUTH } from "@/constants/testIds";
import {
  Home, Calendar, Briefcase, ShieldCheck, LogOut, Menu, X, Sparkles, MapPin,
  BarChart3, Image, FileText,
} from "lucide-react";
import { useState, useEffect } from "react";
import { api } from "@/lib/api";

const TL_LOGO = "/brand/tl-logo.png";

export function Logo({ size = "default", invert = false }) {
  const subColor = invert ? "text-white/70" : "text-zinc-500";
  return (
    <Link to="/" className="group flex flex-col leading-none gap-2" data-testid="brand-logo">
      <img
        src={TL_LOGO}
        alt="Travel + Leisure"
        className={`${size === "lg" ? "h-10 sm:h-12" : "h-7 sm:h-8"} w-auto self-start select-none`}
        style={invert ? { filter: "invert(1)", mixBlendMode: "screen" } : undefined}
        draggable={false}
      />
      <span className={`text-[7px] sm:text-[8px] uppercase tracking-[0.18em] font-medium ${subColor} whitespace-nowrap`}>
        Strategic Supplier Summit 2026
      </span>
    </Link>
  );
}

const NAV = [
  { to: "/", label: "Home", icon: Home },
  { to: "/impact", label: "Impact", icon: BarChart3 },
  { to: "/highlights", label: "Highlights", icon: Image },
  { to: "/resources", label: "Resources", icon: FileText },
];

// Compact horizontal nav activates at lg (1024px+). Below that, the mobile
// slide-down menu is used so the Register CTA never overflows on tablet.

export default function Layout({ children, eventName, transparentHeader = false }) {
  // transparentHeader retained for backwards compat but the executive header is now always solid charcoal.
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(eventName);

  useEffect(() => {
    if (eventName) { setName(eventName); return; }
    api.get("/event").then(({ data }) => setName(data.name)).catch(() => {});
  }, [eventName]);

  const onLogout = () => {
    logout();
    nav("/");
  };

  const isAdmin = user?.role === "admin" || user?.role === "tl_internal";
  const isExhibitor = user?.role === "exhibitor";
  const isSBEAttendee = user?.role === "attendee";
  const canMatchmake = isExhibitor || isSBEAttendee;

  const navLinkClass = "relative text-xs uppercase tracking-[0.18em] font-medium whitespace-nowrap transition-colors";

  return (
    <div className="min-h-screen flex flex-col">
      {/* Executive header — solid charcoal with champagne hairline */}
      <header className="sticky top-0 z-40 bg-[#0A0A0A]/95 backdrop-blur-md border-b border-[#A39171]/30">
        <div className="container-summit relative flex items-center justify-between h-16 sm:h-20">
          <Logo invert />
          <nav className="hidden lg:flex items-center gap-6 xl:gap-9">
            {NAV.map((n) => {
              const active = loc.pathname === n.to;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  className={`${navLinkClass} ${active ? "text-white" : "text-white/75 hover:text-white"}`}
                >
                  {n.label}
                  {active && <span className="absolute left-0 right-0 -bottom-[26px] h-px bg-[#A39171]" />}
                </Link>
              );
            })}
            {isExhibitor && (
              <Link to="/dashboard" data-testid="nav-dashboard"
                className={`${navLinkClass} text-white/75 hover:text-white`}>
                Dashboard
              </Link>
            )}
            {canMatchmake && (
              <Link to="/matchmaking" data-testid="nav-matchmaking"
                className={`${navLinkClass} text-white/75 hover:text-white`}>
                Matchmaking
              </Link>
            )}
            {user && (
              <Link to="/documents" data-testid="nav-documents"
                className={`${navLinkClass} text-white/75 hover:text-white`}>
                Documents
              </Link>
            )}
            {isAdmin && (
              <Link to="/admin" data-testid="nav-admin"
                className={`${navLinkClass} text-white/75 hover:text-white`}>
                Admin
              </Link>
            )}
          </nav>
          <div className="hidden lg:flex items-center gap-3 pl-5 xl:pl-8 ml-2 xl:ml-4 border-l border-white/10">
            {user ? (
              <button onClick={onLogout} data-testid={AUTH.logoutBtn}
                className="text-xs uppercase tracking-[0.18em] font-medium whitespace-nowrap text-white/75 hover:text-white flex items-center gap-2 transition-colors">
                <LogOut size={14} /> Sign Out
              </button>
            ) : (
              <Link to="/login" data-testid="nav-login"
                className="h-10 px-5 xl:px-6 text-xs inline-flex items-center justify-center uppercase tracking-[0.18em] font-medium whitespace-nowrap border border-[#A39171]/60 text-white hover:bg-[#A39171] hover:border-[#A39171] hover:text-black transition-colors">
                Sign In
              </Link>
            )}
            <Link to="/highlights" data-testid="nav-register"
              className="h-10 px-6 xl:px-7 text-xs inline-flex items-center justify-center uppercase tracking-[0.18em] font-semibold whitespace-nowrap bg-white text-black hover:bg-[#A39171] hover:text-white transition-colors">
              Event Recap
            </Link>
          </div>
          <button className="lg:hidden text-white p-2 -mr-2 min-h-[44px] min-w-[44px] inline-flex items-center justify-center"
            onClick={() => setOpen(!open)} aria-label="menu" data-testid="nav-mobile-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-[#A39171]/30 bg-[#0A0A0A]">
            <div className="container-summit py-6 flex flex-col gap-4">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  className="flex items-center gap-3 text-sm uppercase tracking-[0.2em] font-medium text-white/90 hover:text-white">
                  <n.icon size={16} /> {n.label}
                </Link>
              ))}
              {isExhibitor && (
                <Link to="/dashboard" onClick={() => setOpen(false)} className="flex items-center gap-3 text-sm uppercase tracking-[0.2em] font-medium text-white/90">
                  <Briefcase size={16} /> Dashboard
                </Link>
              )}
              {canMatchmake && (
                <Link to="/matchmaking" onClick={() => setOpen(false)} className="flex items-center gap-3 text-sm uppercase tracking-[0.2em] font-medium text-white/90">
                  <Sparkles size={16} /> Matchmaking
                </Link>
              )}
              {user && (
                <Link to="/documents" onClick={() => setOpen(false)} className="flex items-center gap-3 text-sm uppercase tracking-[0.2em] font-medium text-white/90">
                  <Briefcase size={16} /> Documents
                </Link>
              )}
              {isAdmin && (
                <Link to="/admin" onClick={() => setOpen(false)} className="flex items-center gap-3 text-sm uppercase tracking-[0.2em] font-medium text-white/90">
                  <ShieldCheck size={16} /> Admin
                </Link>
              )}
              <div className="flex gap-3 pt-4 border-t border-[#A39171]/20">
                {user ? (
                  <button onClick={onLogout} className="flex-1 h-11 text-xs uppercase tracking-[0.2em] font-medium border border-[#A39171]/60 text-white hover:bg-[#A39171] hover:text-black">Sign Out</button>
                ) : (
                  <Link to="/login" onClick={() => setOpen(false)} className="flex-1 h-11 inline-flex items-center justify-center text-xs uppercase tracking-[0.2em] font-medium border border-[#A39171]/60 text-white hover:bg-[#A39171] hover:text-black">Sign In</Link>
                )}
                <Link to="/highlights" onClick={() => setOpen(false)} className="flex-1 h-11 inline-flex items-center justify-center text-xs uppercase tracking-[0.2em] font-semibold bg-white text-black hover:bg-[#A39171] hover:text-white">Event Recap</Link>
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 pb-28 lg:pb-12">{children}</main>

      {/* Mobile bottom nav — activates below lg. Contrast bumped, safe-area padded, min 44px touch. */}
      <nav
        className="lg:hidden fixed bottom-4 inset-x-4 z-40 glass-nav rounded-full border border-black/10 px-2 py-2 flex items-center justify-around shadow-lg"
        style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}
      >
        {NAV.slice(0, 5).map((n) => {
          const active = loc.pathname === n.to;
          return (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`bottom-nav-${n.label.toLowerCase()}`}
              className={`flex flex-col items-center justify-center gap-1 px-2 py-1 min-h-[44px] min-w-[44px] rounded-full transition-colors ${
                active ? "text-black font-semibold" : "text-zinc-600 hover:text-black"
              }`}
            >
              <n.icon size={18} strokeWidth={active ? 2 : 1.6} />
              <span className="text-[10px] uppercase tracking-[0.14em]">{n.label}</span>
            </Link>
          );
        })}
      </nav>

      <footer className="border-t border-[hsl(var(--line))] py-10 mt-12">
        <div className="container-summit grid gap-6 md:grid-cols-3 items-start">
          <div>
            <div className="serif text-lg">Strategic Supplier Summit</div>
            <div className="text-xs text-zinc-500 mt-1">Produced by Display America</div>
            <a href="mailto:info@displayamerica.com" className="text-xs text-zinc-500 mt-3 inline-block underline-offset-4 hover:underline"
              data-testid="footer-support-email">info@displayamerica.com</a>
          </div>
          <div className="md:justify-self-center flex flex-wrap gap-6 text-xs uppercase tracking-[0.18em] text-zinc-500">
            <Link to="/contact" data-testid="footer-contact" className="hover:text-black">Contact</Link>
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-black">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-black">Terms of Use</Link>
          </div>
          <div className="md:justify-self-end text-[11px] uppercase tracking-[0.22em] text-zinc-400">
            © {new Date().getFullYear()} Display America
          </div>
        </div>
      </footer>
    </div>
  );
}
